import { useMutation, useQuery } from "@apollo/client";
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { BulkActionSubmission } from "components/BulkActions";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  StaffUsersForAssignmentQuery,
  StaffUsersForAssignmentQueryVariables,
  SubmissionStatus,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { BULK_CREATE_REVISION_SUBMISSIONS_MUTATION } from "queries/submissions";
import { useMemo, useState } from "react";
import { STAFF_USERS_QUERY } from "routes/Staff/WasteFulfillment/api";
import { toUtcMidnightIsoFromDateLike } from "util/dates";

type BulkCreateRevisionSubmissionsDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: BulkActionSubmission[];
  onSuccess: () => void;
  refetchQueries: string[];
};

export function BulkCreateRevisionSubmissionsDialog({
  open,
  onClose,
  submissions,
  onSuccess,
  refetchQueries,
}: BulkCreateRevisionSubmissionsDialogProps) {
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);
  const [status, setStatus] = useState<SubmissionStatus>(SubmissionStatus.InProgress);
  const [staffAssigneeSearch, setStaffAssigneeSearch] = useState("");
  const [selectedStaffAssignee, setSelectedStaffAssignee] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const alerts = useAlerts();

  const { data: staffUsersData, loading: staffUsersLoading } = useQuery<
    StaffUsersForAssignmentQuery,
    StaffUsersForAssignmentQueryVariables
  >(STAFF_USERS_QUERY, {
    variables: { search: staffAssigneeSearch || null },
    skip: !open,
  });

  const staffUserOptions = useMemo(
    () =>
      (staffUsersData?.staffUsers?.items ?? []).map((u) => {
        const name = u.person ? `${u.person.first} ${u.person.last}` : u.email;
        return {
          id: u.id,
          label: name !== u.email ? `${name} (${u.email})` : u.email,
        };
      }),
    [staffUsersData]
  );

  const [createRevisions, { loading }] = useMutation(BULK_CREATE_REVISION_SUBMISSIONS_MUTATION, {
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const handleClose = () => {
    setSelectedDate(null);
    setStatus(SubmissionStatus.InProgress);
    setSelectedStaffAssignee(null);
    setStaffAssigneeSearch("");
    onClose();
  };

  const submissionIds = useMemo(
    () => submissions.map((s) => s.submissionId).filter((id): id is string => id != null),
    [submissions]
  );

  const handleSubmit = async () => {
    if (submissionIds.length === 0) {
      alerts.error("No eligible submissions selected");
      return;
    }

    try {
      const dueDateIso = selectedDate ? toUtcMidnightIsoFromDateLike(selectedDate) : undefined;

      const result = await createRevisions({
        variables: {
          input: {
            submissionIds,
            dueDate: dueDateIso ?? undefined,
            status,
            staffAssigneeId: selectedStaffAssignee?.id ?? undefined,
          },
        },
      });

      if (result.data?.bulkCreateRevisionSubmissions.succeeded) {
        const created = result.data.bulkCreateRevisionSubmissions.updated ?? 0;
        alerts.success(`Successfully created ${created} revision(s)`);
        handleClose();
        onSuccess();
      } else {
        alerts.error("Failed to create revisions");
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      alerts.error(`Failed to create revisions: ${message}`);
    }
  };

  const tableRows = useMemo(
    () =>
      submissions.map((s) => ({
        id: s.id,
        facility: s.facilityName,
        submissionType: s.submissionType,
        title: s.title,
        status: s.status,
      })),
    [submissions]
  );

  const columns: CustomGridColDef<(typeof tableRows)[number]>[] = [
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "submissionType",
      headerName: "Submission Type",
      width: 150,
      valueGetter: (_value, row) => prettyPrintEnumValue(row.submissionType),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      valueGetter: (_value, row) => prettyPrintEnumValue(row.status),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 900,
            maxWidth: "90vw",
          },
        },
      }}
      title={`Create Revisions for ${submissions.length} Submission(s)`}
      description="This will create revision copies of the selected submissions. The original submissions will remain unchanged."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        loadingText: "Creating...",
        saveText: "Create Revisions",
        disabled: loading || submissions.length === 0 || selectedDate == null,
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <DatePicker
          label="Due Date"
          value={selectedDate}
          onChange={(newValue) => setSelectedDate(newValue)}
          minDate={DateTime.now()}
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />
        <FormControl fullWidth>
          <InputLabel id="revision-status-label">Status</InputLabel>
          <Select<SubmissionStatus>
            labelId="revision-status-label"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value={SubmissionStatus.InProgress}>In Progress</MenuItem>
            <MenuItem value={SubmissionStatus.NotStarted}>Not Started</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Autocomplete
        options={staffUserOptions}
        value={selectedStaffAssignee}
        onChange={(_event, newValue) => setSelectedStaffAssignee(newValue)}
        onInputChange={(_event, newInputValue) => setStaffAssigneeSearch(newInputValue)}
        loading={staffUsersLoading}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Staff Assignee (optional — copies from original if empty)"
          />
        )}
      />

      {submissions.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Submissions to revise ({submissions.length})
          </Typography>
          <DataGrid
            rows={tableRows}
            columns={columns}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={[5, 10]}
            hideFooterPagination={false}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            sx={{ maxHeight: 600 }}
          />
        </Box>
      )}
    </Dialog>
  );
}
